import React, { useMemo, useState } from "react";
import { Button, Card, Input, Popover, Segmented, Space, Tag, Typography, message } from "antd";
import { createHoliday, deleteHoliday } from "../../api/client";
import { apiError } from "../../utils/apiError";
import GxNote from "../../components/GxNote";
import { JALALI_MONTHS, jalaaliMonthLength, jalaaliToISO } from "../../utils/dateUtils";
import { faDigits } from "../../utils/faNumberFormat";

const { Text } = Typography;
const WEEK_SHORT = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

/**
 * تقویمِ جدولیِ ماه برای تعریفِ تعطیلی‌ها.
 *
 * چرا جدول و نه فهرست: کاربر می‌خواهد «مثل تقویم» یک نگاه بیندازد و ببیند کدام
 * روزها تعطیل‌اند. در فهرستِ متنی باید تاریخ‌ها را در ذهن به روزِ هفته ترجمه کند.
 *
 * سه لایهٔ داده روی هم می‌نشینند و **اولین شرطِ منطبق** برنده است:
 *   ۱) تعطیلیِ ثبت‌شدهٔ همان روز (باحقوق / کارگاه‌باز)
 *   ۲) روزِ غیرکاریِ برنامهٔ هفتگی (جمعه) — حاشیهٔ خط‌چین، چون از تقویمِ هفته می‌آید
 *   ۳) روزِ کاریِ عادی
 * روپوش‌ها: روزِ ۳۱ (اضافه‌کاری) و روزی که «اصلاحِ همگانی» دارد.
 */
export default function MonthCalendarCard({
  year, month, holidays = [], schedule = [], dayRules = [], locked, onChanged,
}) {
  const [openDay, setOpenDay] = useState(null);
  const [kind, setKind] = useState("paid_off");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const len = jalaaliMonthLength(year, month);
  const firstISO = jalaaliToISO(year, month, 1);
  // شنبه = ستون ۰ (getDay: یکشنبه=۰ … شنبه=۶)
  const offset = (new Date(`${firstISO}T00:00:00`).getDay() + 1) % 7;

  const holByISO = useMemo(() => {
    const m = {};
    holidays.forEach((h) => { m[String(h.holiday_date).slice(0, 10)] = h; });
    return m;
  }, [holidays]);

  const ruleByISO = useMemo(() => {
    const m = {};
    dayRules.forEach((r) => { m[String(r.work_date).slice(0, 10)] = r; });
    return m;
  }, [dayRules]);

  const schedByWeekday = useMemo(() => {
    const m = {};
    schedule.forEach((r) => { m[r.weekday] = r; });
    return m;
  }, [schedule]);

  /** وضعیتِ یک روز — همان ترتیبِ اولویتِ بالا. */
  const dayKind = (iso, jw) => {
    const h = holByISO[iso];
    if (h && h.kind === "working") return "workshop_open";
    if (h) return "paid_off";
    if (schedByWeekday[jw] && schedByWeekday[jw].is_working === false) return "weekly_off";
    return "working";
  };

  const STYLE = {
    workshop_open: { bg: "color-mix(in srgb, var(--warning, #E67E22) 14%, transparent)",
      border: "1px solid var(--warning, #E67E22)", label: "تعطیلِ رسمی — کارگاه باز" },
    paid_off: { bg: "color-mix(in srgb, var(--success, #107C41) 14%, transparent)",
      border: "1px solid var(--success, #107C41)", label: "تعطیلِ باحقوق" },
    weekly_off: { bg: "color-mix(in srgb, var(--success, #107C41) 7%, transparent)",
      border: "1px dashed var(--success, #107C41)", label: "غیرکاریِ هفتگی" },
    working: { bg: "var(--surface, #fff)",
      border: "1px solid var(--border, #e4e8f0)", label: "روزِ کاری" },
  };

  /* ⚠️ تلهٔ نام‌گذاری: در بک‌اند `kind="working"` یعنی «تعطیلِ رسمی ولی کارگاه
     باز»، نه «روزِ کاریِ عادی». روزِ عادی اصلاً رکوردِ تعطیلی ندارد. این تابع
     همان سه حالتِ روی صفحه را به این دو مفهوم ترجمه می‌کند. */
  const save = async (iso, choice) => {
    setBusy(true);
    try {
      const existing = holByISO[iso];
      if (choice === "none") {
        if (existing) await deleteHoliday(existing.id);
      } else {
        // `POST /holidays/` روی همان تاریخ upsert می‌کند، پس حذفِ قبلی لازم نیست
        await createHoliday({ holiday_date: iso, kind: choice, description: desc || null });
      }
      setOpenDay(null); setDesc("");
      message.success("تقویم به‌روز شد");
      onChanged?.();
    } catch (e) { message.error(apiError(e, "ثبت نشد")); }
    setBusy(false);
  };

  const cells = [];
  for (let i = 0; i < offset; i += 1) cells.push(null);
  for (let d = 1; d <= len; d += 1) cells.push(d);

  return (
    <Card size="small"
      title={`تقویمِ ${JALALI_MONTHS[month - 1]} ${faDigits(year)}`}
      extra={
        <Space size={4} wrap>
          {["paid_off", "workshop_open", "weekly_off", "working"].map((k) => (
            <span key={k} style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, display: "inline-block",
                background: STYLE[k].bg, border: STYLE[k].border }} />
              {STYLE[k].label}
            </span>
          ))}
        </Space>
      }>
      <GxNote id="att-holiday-cal" signature="v1"
        message="روی هر روز کلیک کنید تا تعطیلِ باحقوق یا «تعطیلِ رسمی ولی کارگاه باز» شود">
        <b>تعطیلِ باحقوق</b> روزِ کاری نیست و غیبت برایش ثبت نمی‌شود؛ <b>کارگاه باز</b>
        مثلِ روزِ عادی حساب می‌شود. جمعه‌ها از «ساعتِ کاریِ هفته» می‌آیند نه از این
        تقویم — حاشیهٔ خط‌چینشان به همین معناست؛ اگر می‌خواهید عوض شود، همان کارت را
        تغییر دهید. ماهِ ۳۱روزه: روزِ سی‌ویکم به‌صورت یک روزِ اضافه روی حقوقِ ثابت
        پرداخت می‌شود و اینجا با نوارِ طلایی نشان داده شده.
      </GxNote>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6,
        direction: "rtl", marginTop: 8 }}>
        {WEEK_SHORT.map((w) => (
          <div key={w} style={{ textAlign: "center", fontSize: 12, fontWeight: 600,
            color: "var(--text-mute, #999)", paddingBottom: 4 }}>{w}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const iso = jalaaliToISO(year, month, d);
          const jw = (offset + d - 1) % 7;
          const k = dayKind(iso, jw);
          const st = STYLE[k];
          const h = holByISO[iso];
          const rule = ruleByISO[iso];
          const isExtra = d === 31;
          const body = (
            <div style={{
              minHeight: 62, borderRadius: 8, padding: "5px 6px", cursor: locked ? "not-allowed" : "pointer",
              background: st.bg, border: st.border,
              borderTop: isExtra ? "3px solid #D4A017" : st.border,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <b style={{ fontSize: 14 }}>{faDigits(d)}</b>
                {rule ? <span title={rule.note || "اصلاحِ همگانی"} style={{ fontSize: 11 }}>⏱</span> : null}
              </div>
              {h?.description ? (
                <div style={{ fontSize: 10, opacity: 0.75, lineHeight: 1.3 }}>{h.description}</div>
              ) : null}
              {isExtra ? <div style={{ fontSize: 9, color: "#8a6d0b" }}>روزِ اضافه</div> : null}
            </div>
          );
          if (locked) return <div key={iso}>{body}</div>;
          // جمعه از برنامهٔ هفتگی می‌آید — اینجا رکوردِ تعطیلی نسازیم، وگرنه
          // دو منبعِ حقیقت برای یک روز پیدا می‌شود
          if (k === "weekly_off" && !h) {
            return (
              <Popover key={iso} trigger="click"
                content={<div style={{ maxWidth: 240, fontSize: 12 }}>
                  این روز از کارتِ <b>«ساعتِ کاریِ هفته»</b> غیرکاری شده است.
                  برای تغییرش همان کارت را ویرایش کنید.
                </div>}>
                {body}
              </Popover>
            );
          }
          return (
            <Popover key={iso} trigger="click"
              open={openDay === iso}
              onOpenChange={(o) => {
                setOpenDay(o ? iso : null);
                setKind(h ? h.kind : "none");
                setDesc(h?.description || "");
              }}
              content={
                <Space direction="vertical" size={8} style={{ width: 250 }}>
                  <Segmented value={kind} onChange={setKind}
                    options={[
                      { value: "none", label: "روزِ کاری" },
                      { value: "paid_off", label: "تعطیلِ باحقوق" },
                      { value: "working", label: "کارگاه باز" },
                    ]} block />
                  <Input placeholder="مناسبت (اختیاری)" value={desc}
                    onChange={(e) => setDesc(e.target.value)} />
                  <Space>
                    <Button type="primary" size="small" loading={busy}
                      onClick={() => save(iso, kind)}>ثبت</Button>
                    {h && (
                      <Button size="small" danger loading={busy}
                        onClick={() => save(iso, "none")}>حذفِ تعطیلی</Button>
                    )}
                  </Space>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {faDigits(`${year}/${String(month).padStart(2, "0")}/${String(d).padStart(2, "0")}`)}
                  </Text>
                </Space>
              }>
              {body}
            </Popover>
          );
        })}
      </div>
    </Card>
  );
}
